import React from "react";
import { Table, Button, Empty, Dropdown, Modal } from "antd";
import { EditOutlined, DeleteOutlined, MoreOutlined } from "@ant-design/icons";
import { Recipe, RawMaterialAction } from "../../../Types";
import { RecipeIngredientsView } from "./RecipeIngredientsView";
import { PAGE_SIZE } from "../../../Constant";
import { tablePagination } from "../../../components";
import { useSectionAccess } from "../../../Services";

interface Props {
  recipes: Recipe[];
  total: number;
  page: number;
  isLoading: boolean;
  onPageChange: (page: number) => void;
  onEdit: (rec: Recipe) => void;
  onDelete: (rec: Recipe) => void;
}

export const RecipesTable: React.FC<Props> = ({
  recipes,
  total,
  page,
  isLoading,
  onPageChange,
  onEdit,
  onDelete,
}) => {
  const { isAllowed } = useSectionAccess("raw_materials");
  const columns = [
    {
      title: "Product",
      key: "product",
      render: (_: any, rec: Recipe) => (
        <div>
          <span className="font-medium text-gray-800 text-sm">
            {rec.product_title}
          </span>
          <div className="text-[11px] text-gray-400">
            {rec.product_sku_code || "—"} · {rec.product_barcode || "—"}
          </div>
        </div>
      ),
    },
    {
      title: "Yield / Batch Size",
      key: "batch_size",
      render: (_: any, rec: Recipe) => (
        <span className="text-xs text-gray-600 font-medium">
          {rec.batch_size || 1} units
        </span>
      ),
    },
    {
      title: "Retail Price",
      dataIndex: "product_retail_price",
      key: "product_retail_price",
      render: (v: number) => (
        <span className="text-xs font-medium text-green-600">
          Rs. {v?.toFixed(2)}
        </span>
      ),
    },
    {
      title: "Wholesale Price",
      dataIndex: "product_wholesale_price",
      key: "product_wholesale_price",
      render: (v: number) => (
        <span className="text-xs font-medium text-blue-600">
          Rs. {v?.toFixed(2)}
        </span>
      ),
    },
    {
      title: "Product Cost",
      dataIndex: "product_cost_price",
      key: "product_cost_price",
      render: (v: number) => (
        <span className="text-xs text-gray-500 font-semibold">
          Rs. {v?.toFixed(2)}
        </span>
      ),
    },
    {
      title: "Recipe Cost",
      key: "recipe_cost",
      render: (_: any, rec: Recipe) => {
        const totalCost = rec.total_cost || 0;
        const addCost = rec.additional_cost || 0;
        const batchSize = rec.batch_size || 1;
        const unitCost = totalCost / batchSize;
        return (
          <div>
            <span className="text-xs font-semibold text-gray-800 block">
              Rs. {totalCost.toFixed(2)}
            </span>
            {addCost > 0 && (
              <span className="text-[10px] text-gray-400 block">
                (incl. Rs. {addCost.toFixed(2)} additional)
              </span>
            )}
            {batchSize !== 1 && (
              <span className="text-[10px] text-indigo-600 font-medium block">
                Rs. {unitCost.toFixed(2)} / unit
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: "Retail Margin",
      key: "margin_diff",
      render: (_: any, rec: Recipe) => {
        const unitCost = (rec.total_cost || 0) / (rec.batch_size || 1);
        const diff = (rec.product_retail_price || 0) - unitCost;
        const pct = rec.product_retail_price
          ? (diff / rec.product_retail_price) * 100
          : 0;
        return (
          <div>
            <span
              className={`text-xs font-semibold block ${diff < 0 ? "text-red-500" : "text-green-600"}`}
            >
              Rs. {diff?.toFixed(2)}
            </span>
            <span className="text-[10px] text-gray-400 block">
              {pct.toFixed(1)}% margin
            </span>
          </div>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      align: "center" as const,
      render: (_: any, rec: Recipe) => {
        const menuItems = [
          ...(isAllowed(RawMaterialAction.ManageRecipes)
            ? [
                {
                  key: "edit",
                  label: "Edit Recipe",
                  icon: <EditOutlined />,
                  onClick: () => onEdit(rec),
                },
              ]
            : []),
          ...(isAllowed(RawMaterialAction.ManageRecipes)
            ? [
                {
                  key: "delete",
                  label: "Delete Recipe",
                  icon: <DeleteOutlined />,
                  danger: true,
                  onClick: () => {
                    Modal.confirm({
                      title: "Delete Recipe?",
                      content: `Are you sure you want to delete the recipe for "${rec.product_title}"?`,
                      okText: "Delete",
                      okButtonProps: { danger: true },
                      cancelText: "Cancel",
                      onOk: () => onDelete(rec),
                    });
                  },
                },
              ]
            : []),
        ];

        return (
          <Dropdown
            menu={{ items: menuItems }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <Button
              type="text"
              size="small"
              icon={<MoreOutlined />}
              className="text-gray-400 hover:text-gray-700"
            />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div className="bg-white rounded-lg border border-gray-100">
      <Table
        dataSource={recipes}
        columns={columns}
        rowKey="id"
        loading={isLoading}
        pagination={tablePagination({
          current: page,
          pageSize: PAGE_SIZE,
          total,
          onChange: onPageChange,
          itemName: "recipes",
        })}
        scroll={{ x: 1000 }}
        size="middle"
        expandable={{
          expandedRowRender: (record) => (
            <RecipeIngredientsView record={record} />
          ),
          rowExpandable: (record) => record.items && record.items.length > 0,
        }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No recipes added yet."
            />
          ),
        }}
      />
    </div>
  );
};
